import { Component, OnInit, ViewEncapsulation, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { HolidayService } from '../../../services/holiday.service';
import { UserService } from '../../../../shared/services/user.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { HolidayModel } from '../../../interfaces/holiday-data';
import { GridDataResult, PageChangeEvent, GridComponent } from '@progress/kendo-angular-grid';
import { SortDescriptor, State, orderBy, filterBy } from '@progress/kendo-data-query';
import {
    NoDataTypes,
    NoDataSourceType,
    NoDataActionType,
} from 'app/modules/shared/interfaces/no-data';
import { CurrentUserStateService } from '../../../../shared/services/current-user-state.service';
import { ImpersonationService } from '../../../../shared/services/impersonation.service';

@Component({
    selector: 'holiday-hours',
    templateUrl: './holiday-hours.component.html',
    styleUrls: ['./holiday-hours.component.scss'],
})
export class HolidayHoursComponent implements OnInit {
    @ViewChild(GridComponent, { static: false })
    private grid: GridComponent;
    holidayEntrySort: SortDescriptor[];
    pageSize = 10;
    skip = 0;
    take: 10;
    type: 'numeric' | 'input' = 'input';
    holidaySearchTerm: string;
    filteredHolidays: HolidayModel[];
    cShortName: string;
    clientKey: number;
    public view: GridDataResult;
    public gridState: State = {
        sort: [],
        skip: 0,
        take: 10,
    };
    public formGroup: UntypedFormGroup;
    selectedHoliday: HolidayModel;
    private editedRowIndex: number;
    holidayEntries: HolidayModel[];
    showAddButton: boolean;
    addActive: boolean;
    loading: boolean;
    noHolidaysForCompany: boolean;
    noDataType: NoDataTypes;
    error: any;

    constructor(
        private currentUserStateService: CurrentUserStateService,
        private toastService: ToastService,
        private holidayService: HolidayService,
        private impersonationService: ImpersonationService
    ) {}

    public ngOnInit(): void {
        scroll(0, 0);
        this.noDataType = {
            action: NoDataActionType.pageStart,
            searchTerm: '',
            componentTitle: 'Holiday Hours',
            inputSource: NoDataSourceType.table,
        };
        this.holidayEntrySort = [{ field: 'holidayDate', dir: 'desc' }];
        let clientShortName = this.currentUserStateService.currentUser.clientShortName;
        this.cShortName = clientShortName;
        if (this.impersonationService.isImpersonatingClient()) {
            clientShortName = this.impersonationService.impersonateClient;
            this.cShortName = clientShortName;
        }

        this.showAddButton = false;
        this.getHolidays();
    }

    getHolidays() {
        this.loading = true;
        this.holidayService.getHolidays(this.cShortName).subscribe(
            (holidays) => {
                if (holidays && holidays.length) {
                    this.noHolidaysForCompany = false;
                    this.holidayEntries = holidays;
                    this.loadHolidaysIntoGridView();
                } else {
                    this.noHolidaysForCompany = true;
                }
                this.loading = false;
            },
            (error) => {
                this.error = error;
                this.loading = false;
                throw new Error(error);
            }
        );
    }

    holidayPageChange({ skip, take }: PageChangeEvent): void {
        this.skip = skip;
        this.pageSize = take;
        this.loadHolidaysIntoGridView();
    }

    reloadHolidaysIntoGridView() {
        this.getHolidays();
    }

    loadHolidaysIntoGridView() {
        let orderedData = orderBy(this.holidayEntries, this.holidayEntrySort);
        this.view = {
            data: orderedData.slice(this.skip, this.skip + this.pageSize),
            total: orderedData.length,
        };
    }

    public onStateChange(state: State) {
        this.gridState = state;
        this.loadHolidaysIntoGridView();
    }

    filterHolidays(): void {
        let searchTermTokens = this.holidaySearchTerm.split(' ');
        this.filteredHolidays = filterBy(this.holidayEntries, {
            logic: 'or',
            filters: [
                {
                    field: 'holiday',
                    operator: 'startswith',
                    value: searchTermTokens[0],
                    ignoreCase: true,
                },
                {
                    field: 'holidayDate',
                    operator: 'startswith',
                    value: searchTermTokens[0],
                    ignoreCase: true,
                },
                {
                    field: 'nbrOfHours',
                    operator: 'startswith',
                    value: searchTermTokens[0],
                    ignoreCase: true,
                },
            ],
        });
        if (searchTermTokens.length > 1) {
            this.filteredHolidays = filterBy(this.filteredHolidays, {
                logic: 'or',
                filters: [
                    {
                        field: 'holiday',
                        operator: 'startswith',
                        value: searchTermTokens[1],
                        ignoreCase: true,
                    },
                    {
                        field: 'holidayDate',
                        operator: 'startswith',
                        value: searchTermTokens[1],
                        ignoreCase: true,
                    },
                    {
                        field: 'nbrOfHours',
                        operator: 'startswith',
                        value: searchTermTokens[1],
                        ignoreCase: true,
                    },
                ],
            });
        }
        let orderedData = orderBy(this.filteredHolidays, this.holidayEntrySort);
        this.view = {
            data: orderedData.slice(this.skip, this.skip + this.pageSize),
            total: orderedData.length,
        };
        this.updateGrid();
    }

    updateGrid() {
        let orderedData = orderBy(
            this.filteredHolidays ? this.filteredHolidays : this.holidayEntries,
            this.holidayEntrySort
        );
        this.view = {
            data: orderedData.slice(this.skip, this.skip + this.pageSize),
            total: orderedData.length,
        };
    }

    sortChange(sort: SortDescriptor[]): void {
        this.holidayEntrySort = sort;
        this.updateGrid();
    }

    public addHoliday() {
        this.closeEditor(this.grid);
        this.formGroup = new UntypedFormGroup({
            holiday: new UntypedFormControl('', Validators.maxLength(100)),
            nbrOfHours: new UntypedFormControl(
                8.0,
                Validators.compose([Validators.required, Validators.min(0), Validators.max(8)])
            ),
            holidayDate: new UntypedFormControl(new Date()),
            clientKey: new UntypedFormControl(4),
            isDeleted: new UntypedFormControl(false),
            lastMod: new UntypedFormControl(new Date()),
            lastModUserKey: new UntypedFormControl(7270),
        });
        this.grid.addRow(this.formGroup);
    }

    public addHandler({ sender }) {
        this.closeEditor(sender);

        this.formGroup = new UntypedFormGroup({
            holiday: new UntypedFormControl('', Validators.maxLength(100)),
            nbrOfHours: new UntypedFormControl(
                8.0,
                Validators.compose([Validators.required, Validators.min(0), Validators.max(8)])
            ),
            holidayDate: new UntypedFormControl(new Date()),
            clientKey: new UntypedFormControl(4),
            isDeleted: new UntypedFormControl(false),
            lastMod: new UntypedFormControl(new Date()),
            lastModUserKey: new UntypedFormControl(7270),
        });

        sender.addRow(this.formGroup);
    }

    public editHandler({ sender, rowIndex, dataItem }) {
        this.closeEditor(sender);
        this.formGroup = new UntypedFormGroup({
            holidayKey: new UntypedFormControl(dataItem.holidayKey),
            clientKey: new UntypedFormControl(dataItem.clientKey),
            holidayDate: new UntypedFormControl(new Date(dataItem.holidayDate)),
            holiday: new UntypedFormControl(
                dataItem.holiday,
                Validators.compose([Validators.required, Validators.maxLength(100)])
            ),
            isDeleted: new UntypedFormControl(dataItem.isDeleted),
            lastMod: new UntypedFormControl(dataItem.lastMod),
            lastModUserKey: new UntypedFormControl(dataItem.lastModUserKey),
            nbrOfHours: new UntypedFormControl(
                dataItem.nbrOfHours,
                Validators.compose([
                    Validators.required,
                    Validators.min(0),
                    Validators.max(8),
                    Validators.pattern(
                        '((([0]*.?((25)|(50)|(5)|(75))))|([1-7]*.?((25)|(50)|(5)|(75)|(0)|(00)))|(8.00)|([0-8]))'
                    ),
                ])
            ),
        });
        this.editedRowIndex = rowIndex;
        sender.editRow(rowIndex, this.formGroup);
    }

    public cancelHandler({ sender, rowIndex }) {
        this.closeEditor(sender, rowIndex);
    }

    public saveHandler({ sender, rowIndex, formGroup, isNew }) {
        const product: HolidayModel = formGroup.value;

        if (!isNew) {
            this.holidayService.updateHoliday(product).subscribe(
                () => {
                    sender.closeRow(rowIndex);
                    this.toastService.success('Holiday ' + product.holiday + ' updated.');
                    this.reloadHolidaysIntoGridView();
                },
                () => {
                    this.toastService.error('Error updating ' + product.holiday + ' holiday.');
                }
            );
        } else {
            this.holidayService.insertHoliday(product).subscribe(
                () => {
                    this.toastService.success('Holiday ' + product.holiday + ' created.');
                    sender.closeRow(rowIndex);
                    this.reloadHolidaysIntoGridView();
                },
                () => {
                    this.toastService.error('Error creating ' + product.holiday + ' holiday.');
                }
            );
        }
    }

    public removeHandler({ dataItem }) {
        this.holidayService.deleteHoliday(dataItem).subscribe(
            () => {
                this.toastService.success('Holiday ' + dataItem.holiday + ' deleted.');
                this.reloadHolidaysIntoGridView();
            },
            () => {
                this.toastService.error('Error deleting ' + dataItem.holiday + ' holiday.');
            }
        );
    }

    private closeEditor(grid, rowIndex = this.editedRowIndex) {
        grid.closeRow(rowIndex);
        this.editedRowIndex = undefined;
        this.formGroup = undefined;
    }
}
